import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  AlertTriangle, CheckCircle, Clock, Zap, Droplets, Wifi,
  Users, Package, DollarSign, MessageCircle, Calendar,
  Settings, TrendingUp, Activity, Bell, Play, Square,
  RefreshCw, Send, Phone, MapPin, Sun, Moon, Coffee
} from 'lucide-react';

// Action-oriented dashboard for RUNNING a pousada
// Not visualization - pure operational efficiency

interface Task {
  id: string;
  text: string;
  priority: 'critical' | 'high' | 'normal';
  category: string;
  time?: string;
  completed?: boolean;
  assignee?: string;
}

interface Alert {
  id: string;
  type: 'critical' | 'warning' | 'info';
  message: string;
  action?: string;
  timestamp: Date;
}

interface Booking {
  id: string;
  guest: string;
  room: string;
  checkIn: string;
  checkOut: string;
  status: 'arriving' | 'in-house' | 'departing';
  requests?: string[];
}

const PousadaOperations: React.FC = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [activeShift, setActiveShift] = useState<'morning' | 'afternoon' | 'evening'>('morning');
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // MOCK DATA - In production, this comes from your database
  const alerts: Alert[] = [
    { id: '1', type: 'critical', message: 'Water pressure low in Block B', action: 'Check pump', timestamp: new Date() },
    { id: '2', type: 'warning', message: 'Breakfast supplies running low', action: 'Order today', timestamp: new Date() },
    { id: '3', type: 'info', message: '5 checkouts today', action: 'Prep rooms', timestamp: new Date() }
  ];

  const todayTasks: Task[] = [
    // Morning shift
    { id: '1', text: 'Check water tanks', priority: 'critical', category: 'infrastructure', time: '06:00' },
    { id: '2', text: 'Start coffee service', priority: 'high', category: 'food', time: '06:30' },
    { id: '3', text: 'Beach chair setup', priority: 'normal', category: 'beach', time: '07:00' },
    { id: '4', text: 'Breakfast prep', priority: 'high', category: 'food', time: '07:00' },
    { id: '5', text: 'Pool chemical check', priority: 'high', category: 'maintenance', time: '07:30' },
    
    // Afternoon shift
    { id: '6', text: 'Room turnover (5 units)', priority: 'critical', category: 'housekeeping', time: '14:00' },
    { id: '7', text: 'Restock mini-bars', priority: 'normal', category: 'housekeeping', time: '15:00' },
    { id: '8', text: 'Confirm tomorrow arrivals', priority: 'high', category: 'reception', time: '15:00' },
    
    // Evening shift
    { id: '9', text: 'Sunset bar setup', priority: 'normal', category: 'bar', time: '17:30' },
    { id: '10', text: 'Night security briefing', priority: 'high', category: 'security', time: '19:00' },
    { id: '11', text: 'Lock pool area', priority: 'critical', category: 'security', time: '22:00' }
  ];

  const todayBookings: Booking[] = [
    { id: '1', guest: 'Silva Family', room: '12', checkIn: '2024-01-15', checkOut: '2024-01-20', status: 'arriving', requests: ['Late checkout', 'Extra towels'] },
    { id: '2', guest: 'John & Mary', room: '7', checkIn: '2024-01-10', checkOut: '2024-01-15', status: 'departing' },
    { id: '3', guest: 'Carlos Santos', room: '3', checkIn: '2024-01-12', checkOut: '2024-01-18', status: 'in-house', requests: ['Massage 16:00'] }
  ];

  // Quick action handlers
  const quickActions = [
    { icon: Phone, label: 'Call Supplier', action: () => console.log('Opening contacts...') },
    { icon: MessageCircle, label: 'WhatsApp Guests', action: () => console.log('Opening WhatsApp...') },
    { icon: Package, label: 'Check Inventory', action: () => console.log('Opening inventory...') },
    { icon: Users, label: 'Staff Schedule', action: () => console.log('Opening schedule...') },
    { icon: DollarSign, label: 'Today\'s Revenue', action: () => console.log('Opening revenue...') },
    { icon: Settings, label: 'Maintenance Log', action: () => console.log('Opening maintenance...') }
  ];

  const criticalMetrics = {
    occupancy: 75,
    waterLevel: 65,
    wifiStatus: 'online',
    powerStatus: 'normal',
    staffPresent: '6/7',
    revenue: 'R$ 3,450'
  };

  const getTasksByShift = (shift: string) => {
    const timeRanges: { [key: string]: string[] } = {
      morning: ['06:00', '12:00'],
      afternoon: ['12:00', '18:00'],
      evening: ['18:00', '23:59']
    };
    
    return todayTasks.filter(task => {
      if (!task.time) return false;
      const [start, end] = timeRanges[shift];
      return task.time >= start && task.time < end;
    });
  };

  const getPriorityColor = (priority: string) => {
    switch(priority) {
      case 'critical': return 'text-red-500 bg-red-500/10 border-red-500/30';
      case 'high': return 'text-orange-500 bg-orange-500/10 border-orange-500/30';
      default: return 'text-blue-500 bg-blue-500/10 border-blue-500/30';
    }
  };

  const getAlertIcon = (type: string) => {
    switch(type) {
      case 'critical': return <AlertTriangle className="w-5 h-5 text-red-500" />;
      case 'warning': return <Clock className="w-5 h-5 text-yellow-500" />;
      default: return <Bell className="w-5 h-5 text-blue-500" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header Bar - Critical Status */}
      <div className="bg-gray-800 border-b border-gray-700 px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            <h1 className="text-xl font-bold">Pousada Operations</h1>
            <div className="text-2xl font-mono text-blue-400">
              {currentTime.toLocaleTimeString('pt-BR')}
            </div>
          </div>
          
          {/* Critical Metrics Bar */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-gray-400" />
              <span className="text-sm">{criticalMetrics.staffPresent}</span>
            </div>
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-gray-400" />
              <span className="text-sm">{criticalMetrics.occupancy}%</span>
            </div>
            <div className="flex items-center gap-2">
              <Droplets className="w-4 h-4 text-gray-400" />
              <span className={`text-sm ${criticalMetrics.waterLevel < 50 ? 'text-red-400' : ''}`}>
                {criticalMetrics.waterLevel}%
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Wifi className={`w-4 h-4 ${criticalMetrics.wifiStatus === 'online' ? 'text-green-400' : 'text-red-400'}`} />
            </div>
            <div className="flex items-center gap-2">
              <Zap className={`w-4 h-4 ${criticalMetrics.powerStatus === 'normal' ? 'text-green-400' : 'text-red-400'}`} />
            </div>
            <div className="text-green-400 font-medium">{criticalMetrics.revenue}</div>
          </div>
        </div>
      </div>

      <div className="flex h-[calc(100vh-60px)]">
        {/* Left Sidebar - Alerts & Quick Actions */}
        <div className="w-80 bg-gray-800 border-r border-gray-700 p-4 overflow-y-auto">
          {/* Alerts */}
          <div className="mb-6">
            <h2 className="text-sm font-semibold text-gray-400 uppercase mb-3">Active Alerts</h2>
            <div className="space-y-2">
              {alerts.map(alert => (
                <motion.div
                  key={alert.id}
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  className={`p-3 rounded-lg border ${
                    alert.type === 'critical' ? 'bg-red-900/20 border-red-500/30' :
                    alert.type === 'warning' ? 'bg-yellow-900/20 border-yellow-500/30' :
                    'bg-blue-900/20 border-blue-500/30'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {getAlertIcon(alert.type)}
                    <div className="flex-1">
                      <p className="text-sm">{alert.message}</p>
                      {alert.action && (
                        <button className="text-xs text-blue-400 hover:text-blue-300 mt-1">
                          → {alert.action}
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div>
            <h2 className="text-sm font-semibold text-gray-400 uppercase mb-3">Quick Actions</h2>
            <div className="grid grid-cols-2 gap-2">
              {quickActions.map((action, i) => (
                <button
                  key={i}
                  onClick={action.action}
                  className="p-3 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors flex flex-col items-center gap-2"
                >
                  <action.icon className="w-5 h-5 text-blue-400" />
                  <span className="text-xs">{action.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content - Tasks & Operations */}
        <div className="flex-1 p-6 overflow-y-auto">
          {/* Shift Selector */}
          <div className="flex items-center gap-4 mb-6">
            <div className="flex bg-gray-800 rounded-lg p-1">
              {(['morning', 'afternoon', 'evening'] as const).map(shift => (
                <button
                  key={shift}
                  onClick={() => setActiveShift(shift)}
                  className={`px-4 py-2 rounded-md transition-colors ${
                    activeShift === shift 
                      ? 'bg-blue-600 text-white' 
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {shift === 'morning' && <Sun className="w-4 h-4" />}
                    {shift === 'afternoon' && <Sun className="w-4 h-4" />}
                    {shift === 'evening' && <Moon className="w-4 h-4" />}
                    <span className="capitalize">{shift}</span>
                  </div>
                </button>
              ))}
            </div>
            <button className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg flex items-center gap-2">
              <Play className="w-4 h-4" />
              Start Shift Checklist
            </button>
          </div>

          {/* Task Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Current Tasks */}
            <div className="bg-gray-800 rounded-xl p-5">
              <h3 className="font-semibold mb-4 flex items-center justify-between">
                <span>{activeShift.charAt(0).toUpperCase() + activeShift.slice(1)} Tasks</span>
                <span className="text-sm text-gray-400">
                  {getTasksByShift(activeShift).filter(t => !t.completed).length} pending
                </span>
              </h3>
              <div className="space-y-2">
                {getTasksByShift(activeShift).map(task => (
                  <div
                    key={task.id}
                    className={`p-3 rounded-lg border flex items-center justify-between cursor-pointer hover:bg-gray-700/50 transition-colors ${
                      task.completed ? 'opacity-50' : ''
                    } ${getPriorityColor(task.priority)}`}
                    onClick={() => setSelectedTask(task)}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={task.completed}
                        className="w-4 h-4"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div>
                        <p className="text-sm font-medium">{task.text}</p>
                        <p className="text-xs opacity-70">{task.time} • {task.category}</p>
                      </div>
                    </div>
                    {task.assignee && (
                      <span className="text-xs bg-gray-700 px-2 py-1 rounded">
                        {task.assignee}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Today's Bookings */}
            <div className="bg-gray-800 rounded-xl p-5">
              <h3 className="font-semibold mb-4">Today's Bookings</h3>
              <div className="space-y-3">
                {todayBookings.map(booking => (
                  <div key={booking.id} className="p-3 bg-gray-700 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{booking.guest}</span>
                      <span className={`text-xs px-2 py-1 rounded ${
                        booking.status === 'arriving' ? 'bg-green-500/20 text-green-400' :
                        booking.status === 'departing' ? 'bg-red-500/20 text-red-400' :
                        'bg-blue-500/20 text-blue-400'
                      }`}>
                        {booking.status}
                      </span>
                    </div>
                    <div className="text-xs text-gray-400">
                      Room {booking.room} • {booking.checkIn} → {booking.checkOut}
                    </div>
                    {booking.requests && (
                      <div className="mt-2 text-xs text-yellow-400">
                        {booking.requests.map((req, i) => (
                          <div key={i}>• {req}</div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Status Grid - Live Monitoring */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="text-sm text-gray-400 mb-1">Rooms Ready</div>
              <div className="text-2xl font-bold">8/12</div>
              <div className="text-xs text-yellow-400">4 in cleaning</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="text-sm text-gray-400 mb-1">Breakfast</div>
              <div className="text-2xl font-bold">45%</div>
              <div className="text-xs text-green-400">Serving now</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="text-sm text-gray-400 mb-1">Beach Service</div>
              <div className="text-2xl font-bold">Active</div>
              <div className="text-xs">15 chairs out</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="text-sm text-gray-400 mb-1">Next Arrival</div>
              <div className="text-2xl font-bold">14:30</div>
              <div className="text-xs">Silva Family</div>
            </div>
          </div>
        </div>

        {/* Right Sidebar - Communication */}
        <div className="w-80 bg-gray-800 border-l border-gray-700 p-4">
          <h2 className="text-sm font-semibold text-gray-400 uppercase mb-3">Team Chat</h2>
          <div className="bg-gray-700 rounded-lg p-3 mb-4 h-64 overflow-y-auto">
            <div className="space-y-2 text-sm">
              <div className="text-blue-400">Maria (Housekeeping): Room 7 ready ✓</div>
              <div className="text-green-400">João (Maintenance): Pool pH adjusted</div>
              <div className="text-yellow-400">Ana (Reception): Late checkout approved for room 12</div>
            </div>
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Type message..."
              className="flex-1 bg-gray-700 px-3 py-2 rounded-lg text-sm"
            />
            <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg">
              <Send className="w-4 h-4" />
            </button>
          </div>
          
          <h2 className="text-sm font-semibold text-gray-400 uppercase mb-3 mt-6">Shift Notes</h2>
          <textarea
            className="w-full bg-gray-700 rounded-lg p-3 text-sm h-32"
            placeholder="Add notes for next shift..."
          />
        </div>
      </div>
    </div>
  );
};

export default PousadaOperations;